import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout, getUser, getPearchCredits } from '../services/api';
import './Sidebar.css';

const CREDIT_EXPIRATION_KEYS = [
  'credits_expire_at',
  'credits_expiration',
  'credits_expires_at',
  'credit_expires_at',
  'credit_expiration',
  'search_credits_expire_at',
  'subscription_expires_at',
  'credits_valid_until',
  'credit_valid_until',
  'expires_at',
  'expiration_date',
];

function pickCurrentPeriodEndFromSubscriptions(raw) {
  const subs = raw.active_subscriptions;
  if (!Array.isArray(subs) || subs.length === 0) return null;
  const ends = [];
  for (const sub of subs) {
    if (!sub || typeof sub !== 'object') continue;
    const cpe = sub.current_period_end;
    if (cpe != null && cpe !== '') ends.push(Number(cpe));
    const items = sub.items;
    if (Array.isArray(items)) {
      for (const item of items) {
        if (item && typeof item === 'object') {
          const icpe = item.current_period_end;
          if (icpe != null && icpe !== '') ends.push(Number(icpe));
        }
      }
    }
  }
  const finite = ends.filter((n) => Number.isFinite(n));
  if (finite.length === 0) return null;
  return Math.max(...finite);
}

function pickCreditExpirationRaw(data) {
  if (!data || typeof data !== 'object') return null;
  const direct = data.credits_expires_at;
  if (direct != null && direct !== '') return direct;
  const raw = data._raw;
  if (raw && typeof raw === 'object') {
    const fromPeriod = pickCurrentPeriodEndFromSubscriptions(raw);
    if (fromPeriod != null) return fromPeriod;
    for (const k of CREDIT_EXPIRATION_KEYS) {
      const v = raw[k];
      if (v != null && v !== '') return v;
    }
    for (const nest of ['subscription', 'plan', 'billing', 'account']) {
      const sub = raw[nest];
      if (sub && typeof sub === 'object') {
        for (const k of CREDIT_EXPIRATION_KEYS) {
          const v = sub[k];
          if (v != null && v !== '') return v;
        }
      }
    }
  }
  return null;
}

/** @param {string|number|null|undefined} raw */
function formatCreditExpiration(raw) {
  if (raw == null || raw === '') return null;
  let ms;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    ms = raw > 1e12 ? raw : raw * 1000;
  } else {
    const s = String(raw).trim();
    const asNum = Number(s);
    if (s !== '' && Number.isFinite(asNum) && !Number.isNaN(asNum)) {
      ms = asNum > 1e12 ? asNum : asNum * 1000;
    } else {
      ms = Date.parse(s);
    }
  }
  if (ms == null || Number.isNaN(ms)) return null;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function Sidebar({ isOpen = false, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = getUser();
  const [credits, setCredits] = useState(null);
  const [creditsExpireLabel, setCreditsExpireLabel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getPearchCredits()
      .then((data) => {
        if (cancelled) return;
        // Log the full payload so it's easy to see what Pearch returns
        console.debug('[Pearch credits] raw response:', data);
        const value =
          data.credits_remaining ??
          data._raw?.credits_remaining ??
          data._raw?.credits ??
          data._raw?.remaining_credits ??
          data._raw?.balance ??
          data._raw?.search_credits ??
          null;
        console.debug('[Pearch credits] resolved value:', value);
        setCredits(value);
        const expireRaw = pickCreditExpirationRaw(data);
        setCreditsExpireLabel(formatCreditExpiration(expireRaw));
      })
      .catch((err) => console.debug('[Pearch credits] error:', err));
    return () => { cancelled = true; };
  }, []);
  const displayName = currentUser
    ? (currentUser.first_name && currentUser.last_name
        ? `${currentUser.first_name} ${currentUser.last_name}`
        : currentUser.username || 'User')
    : 'User';
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Still redirect to login even if API call fails
      navigate('/login');
    }
  };

  return (
    <>
    <div
      className={`sidebar-backdrop ${isOpen ? 'visible' : ''}`}
      onClick={onClose}
    />
    <div className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <div className="logo-square">LS</div>
          <div className="logo-text">
            <div className="logo-title">LegalSoft</div>
            <div className="logo-subtitle">AI Matchmaker</div>
          </div>
        </div>
      </div>
      
      <nav className="sidebar-nav">
        <Link 
          to="/" 
          className={`nav-item ${isActive('/') ? 'active' : ''}`}
        >
          <span className="nav-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z" fill="currentColor"/>
            </svg>
          </span>
          <span className="nav-label">Dashboard</span>
        </Link>
        <Link 
          to="/romeo" 
          className={`nav-item ${isActive('/romeo') ? 'active' : ''}`}
        >
          <span className="nav-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4ZM20 19H4V8H20V19Z" fill="currentColor"/>
            </svg>
          </span>
          <span className="nav-label">Romeo (Jobs)</span>
        </Link>
        <Link 
          to="/juliet" 
          className={`nav-item ${isActive('/juliet') ? 'active' : ''}`}
        >
          <span className="nav-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 7C16 9.21 14.21 11 12 11C9.79 11 8 9.21 8 7C8 4.79 9.79 3 12 3C14.21 3 16 4.79 16 7ZM12 9C13.1 9 14 8.1 14 7C14 5.9 13.1 5 12 5C10.9 5 10 5.9 10 7C10 8.1 10.9 9 12 9ZM12 13C14.67 13 20 14.33 20 17V19H4V17C4 14.33 9.33 13 12 13ZM12 11C9.33 11 4 12.33 4 15V17H20V15C20 12.33 14.67 11 12 11Z" fill="currentColor"/>
            </svg>
          </span>
          <span className="nav-label">Juliet (Candidates)</span>
        </Link>
        <Link
          to="/search"
          className={`nav-item ${isActive('/search') ? 'active' : ''}`}
        >
          <span className="nav-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
            </svg>
          </span>
          <span className="nav-label">Search</span>
        </Link>
        <Link 
          to="/matchmaker" 
          className={`nav-item ${location.pathname.startsWith('/matchmaker') ? 'active' : ''}`}
        >
          <span className="nav-icon matchmaker-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor"/>
              <path d="M12 17.5l-0.72-0.66C8.28 14.42 6 12.64 6 10.5 6 8.79 7.21 7.5 9 7.5c1.04 0 2.05.49 2.72 1.25L12 9.03l1.28-1.28C13.95 7.49 14.96 7 16 7c1.79 0 3 1.29 3 3.5 0 2.14-2.28 3.92-5.28 6.34L12 17.5z" fill="white" opacity="0.3"/>
            </svg>
          </span>
          <span className="nav-label">Matchmaker</span>
        </Link>
        <Link 
          to="/admin/team" 
          className={`nav-item ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
        >
          <span className="nav-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
            </svg>
          </span>
          <span className="nav-label">Team</span>
        </Link>
      </nav>

      <div className="sidebar-footer">
        {credits != null && (() => {
          const level = credits === 0 ? 'critical' : credits < 4000 ? 'low' : 'healthy';
          const icon = {
            critical: (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
              </svg>
            ),
            low: (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="currentColor"/>
              </svg>
            ),
            healthy: (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M11.5 2C6.81 2 3 5.81 3 10.5S6.81 19 11.5 19h.5v3c4.86-2.34 8-7 8-11.5C20 5.81 16.19 2 11.5 2zm1 14.5h-2v-2h2v2zm0-4h-2c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2h-2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.5-3 2.75-3 5z" fill="currentColor"/>
              </svg>
            ),
          }[level];
          return (
            <div className="credits-block">
              <div className={`credits-bar credits-bar--${level}`}>
                {icon}
                <div className="credits-bar-body">
                  <div className="credits-remaining-row">
                    <span className="credits-value" title="Search credits balance">
                      {credits.toLocaleString()}
                    </span>
                    <span className="credits-remaining-suffix">credits remaining</span>
                    <span className="credits-info-wrap">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="currentColor" />
                      </svg>
                      <span className="credits-tooltip">
                        Search and match features use these credits. On average, a search consumes ~100 credits.
                        {creditsExpireLabel && (
                          <>
                            <br />
                            Credits expire {creditsExpireLabel}.
                          </>
                        )}
                      </span>
                    </span>
                  </div>
                  <div className="credits-product-hint">Pearch search credits</div>
                </div>
              </div>
              {creditsExpireLabel && (
                <div className={`credits-expiry credits-expiry--${level}`}>
                  Expires {creditsExpireLabel}
                </div>
              )}
            </div>
          );
        })()}
        <div className="sidebar-footer-row">
          <div className="user-profile">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <div className="user-name">{displayName}</div>
              <div className="user-role">Admin</div>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout} title="Logout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

export default Sidebar;
